/**
 * Срок годности лекарства.
 *
 * У вскрытой упаковки сроков два. На коробке стоит один — «годен до 03.2027»,
 * — а с момента вскрытия начинает идти второй, короткий: «использовать в
 * течение 28 дней». Годен препарат до более раннего из них: капли, вскрытые за
 * полгода до конца срока, к концу срока давно не годны, и коробка об этом
 * молчит. Практика NHS для капель и мазей ровно такая: дату вскрытия
 * записывают на флаконе и выбрасывают его через оговорённый срок.
 *
 * Ничего не выдумываем: нет даты — значит «срок не указан». Приложение,
 * придумавшее срок годности за человека, однажды разрешит принять
 * просроченное.
 */

import type { Medication } from '@/shared/db/schema';
import { addDays, diffDays, formatDateFull, type IsoDate, plural, today } from '@/shared/lib/datetime';

/** Что со сроком: не указан, в порядке, скоро истечёт, истёк. */
export type ExpiryStatus = 'none' | 'ok' | 'soon' | 'expired';

/**
 * За сколько дней начинать предупреждать.
 *
 * Месяц — срок, за который успевают купить замену и не выбрасывать начатое
 * раньше времени. Короче — человек узнаёт о конце срока, когда лекарство уже
 * нужно; длиннее — предупреждение висит месяцами и его перестают замечать.
 */
export const EXPIRY_SOON_DAYS = 30;

export interface ExpiryState {
  status: ExpiryStatus;
  /** До какой даты годен с учётом вскрытия. `null` — неизвестно. */
  date: IsoDate | null;
  /** Сколько дней осталось; отрицательное — просрочено. `null` — неизвестно. */
  daysLeft: number | null;
  /** Срок задан вскрытием, а не коробкой: об этом стоит сказать словами. */
  fromOpening: boolean;
}

/** Поля, из которых считается срок: карточка препарата или её черновик. */
type ExpiryFields = Pick<Medication, 'expiryDate' | 'openedAt' | 'openedShelfDays'>;

/** Дата, после которой вскрытую упаковку пора выбросить. */
function openedUntil(medication: ExpiryFields): IsoDate | null {
  const { openedAt, openedShelfDays } = medication;
  // «28 дней» сами по себе ни от чего не отсчитываются.
  if (!openedAt || openedShelfDays === null || openedShelfDays <= 0) return null;

  // День вскрытия входит в срок: вскрытый сегодня флакон на 28 дней годен по
  // 27-й день после вскрытия включительно.
  return addDays(openedAt, openedShelfDays - 1);
}

/** До какой даты лекарство годно на самом деле. `null` — срок неизвестен. */
export function effectiveExpiry(medication: ExpiryFields): IsoDate | null {
  const printed = medication.expiryDate;
  const opened = openedUntil(medication);

  if (!printed) return opened;
  if (!opened) return printed;
  return opened < printed ? opened : printed;
}

/** Всё о сроке разом: дата, остаток дней и насколько это срочно. */
export function expiryState(medication: ExpiryFields, reference: IsoDate = today()): ExpiryState {
  const date = effectiveExpiry(medication);
  if (!date) return { status: 'none', date: null, daysLeft: null, fromOpening: false };

  const opened = openedUntil(medication);
  const fromOpening = opened !== null && opened === date;

  // Дата на упаковке включительная: в свой последний день лекарство годно.
  const daysLeft = diffDays(reference, date);
  const status: ExpiryStatus =
    daysLeft < 0 ? 'expired' : daysLeft <= EXPIRY_SOON_DAYS ? 'soon' : 'ok';

  return { status, date, daysLeft, fromOpening };
}

/**
 * Срок коротко — для метки в строке аптечки.
 *
 * Метка стоит рядом с названием и остатком, и места в ней на предложение нет.
 * Дальний срок сжимается до месяца и года — ровно так, как он напечатан на
 * упаковке; близкий считается днями, потому что дни здесь и есть ответ; у
 * просроченного важно не число, а само слово.
 */
export function expiryBadge(state: ExpiryState): string {
  if (state.date === null || state.daysLeft === null) return 'Срок не указан';
  if (state.status === 'expired') return 'Просрочено';
  if (state.daysLeft === 0) return 'Последний день';
  if (state.status === 'soon') {
    return `${state.daysLeft} ${plural(state.daysLeft, 'день', 'дня', 'дней')}`;
  }

  return `до ${monthAndYear(state.date)}`;
}

/** «03.2027» — так срок годности и напечатан на коробке. */
function monthAndYear(iso: IsoDate): string {
  const [year, month] = iso.split('-');
  return `${month}.${year}`;
}

/**
 * Строка о сроке — та, что стоит в карточке аптечки.
 *
 * У просроченного называется дата, а не число дней: «просрочено на 43 дня»
 * заставляет считать в уме, а дата сверяется с упаковкой глазами. У близкого
 * срока наоборот важны дни: «осталось 9 дней» — это ответ на вопрос «успею ли
 * допить».
 */
export function describeExpiry(state: ExpiryState): string {
  if (state.date === null || state.daysLeft === null) return 'Срок не указан';

  if (state.status === 'expired') return `Истёк ${formatDateFull(state.date)}`;
  if (state.status === 'ok') return `Годен до ${formatDateFull(state.date)}`;

  const tail = state.fromOpening ? ' после вскрытия' : '';
  if (state.daysLeft === 0) return `Последний день${tail}`;

  const days = state.daysLeft;
  return `Осталось ${days} ${plural(days, 'день', 'дня', 'дней')}${tail}`;
}
